// =============================================================================
// Screenshot Event Parser — Scoring-based candidate extraction.
// Uses bounding-box metadata from OCR lines to rank candidates.
// No AI — same rules are mirrored in ScreenshotEventParser.swift for iOS.
// =============================================================================

const MONTH_NAMES = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

const MONTH_PATTERN = Object.keys(MONTH_NAMES).join('|');

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_ABBREVS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const TZ_MAP = {
  EST: 'America/New_York', EDT: 'America/New_York', ET: 'America/New_York',
  CST: 'America/Chicago', CDT: 'America/Chicago', CT: 'America/Chicago',
  MST: 'America/Denver', MDT: 'America/Denver', MT: 'America/Denver',
  PST: 'America/Los_Angeles', PDT: 'America/Los_Angeles', PT: 'America/Los_Angeles',
  UTC: 'UTC', GMT: 'UTC',
};

const DEFAULT_TIMEZONE = 'America/New_York';
const DEFAULT_DURATION_MINUTES = 60;

const EMAIL_META_RE = /^(from|to|cc|bcc|sent|date|subject|reply-to|re:|fwd:)\s*:?/i;

// =============================================================================
// Main entry point
// =============================================================================

export function parseScreenshotText(ocrData) {
  let lines, headlineLines, rawText;

  if (typeof ocrData === 'string') {
    const textLines = ocrData.split('\n').map(t => t.trim()).filter(Boolean);
    lines = textLines.map((text, i, arr) => ({
      text,
      confidence: 50,
      yNorm: arr.length > 1 ? i / (arr.length - 1) : 0.5,
      heightNorm: 1 / Math.max(arr.length, 1),
    }));
    headlineLines = [];
    rawText = ocrData;
  } else {
    lines = (ocrData.lines || []).filter(l => l.text.trim());
    headlineLines = (ocrData.headlineLines || []).filter(l => l.text.trim());
    rawText = ocrData.text || lines.map(l => l.text).join('\n');
  }

  // 1. Score date & time candidates from all lines
  const dateCandidates = scoreDateCandidates(lines);
  const timeCandidates = scoreTimeCandidates(lines);

  // 2. Pick best coherent (date, time) pair
  const { bestDate, bestTime } = selectCoherentDateTime(dateCandidates, timeCandidates);
  const usedLines = new Set();
  if (bestDate != null) usedLines.add(bestDate.lineIdx);
  if (bestTime != null) usedLines.add(bestTime.lineIdx);

  // 3. Location (excluding lines used for date/time)
  const locationCandidates = scoreLocationCandidates(lines, usedLines);
  const bestLocation = locationCandidates[0] || null;
  if (bestLocation) usedLines.add(bestLocation.lineIdx);

  // 4. Title (excluding lines used for date/time/location)
  const titleCandidates = scoreTitleCandidates(lines, headlineLines, usedLines);
  const bestTitle = titleCandidates[0] || null;

  // 5. Timezone
  const timezone = extractTimezone(rawText);

  // 6. Resolve final values
  const primaryDate = bestDate ? bestDate.date : null;
  const startTime = bestTime ? bestTime.startTime : null;
  const endTime = bestTime
    ? (bestTime.endTime || (startTime ? addMinutes(startTime, DEFAULT_DURATION_MINUTES) : null))
    : null;

  const confidence = computeConfidence(dateCandidates, timeCandidates, startTime);

  const uiDateCandidates = dateCandidates.slice(0, 5).map(c => ({
    date: c.date,
    startTime: c.startTime || startTime,
    endTime: c.endTime || endTime,
    label: c.label,
    score: c.score,
  }));

  return {
    title: bestTitle ? bestTitle.text : null,
    date: primaryDate,
    startTime,
    endTime,
    timezone: timezone || DEFAULT_TIMEZONE,
    location: bestLocation ? bestLocation.text : null,
    notes: rawText,
    confidence,
    dateCandidates: uiDateCandidates,
    timeCandidates: timeCandidates.slice(0, 5).map(c => ({
      startTime: c.startTime,
      endTime: c.endTime,
      label: c.label,
      score: c.score,
    })),
    rawText,
  };
}

// =============================================================================
// Date candidate scoring
// =============================================================================

function scoreDateCandidates(lines) {
  const candidates = [];
  const seen = new Set();

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    const text = line.text;
    const conf = (line.confidence || 50) / 10;
    const metaPen = EMAIL_META_RE.test(text) ? -15 : 0;
    let m;

    // 1. Weekday + Month Day (strongest): "Sunday, February 8"
    const wdRe = new RegExp(
      `(?:${DAY_NAMES.join('|')}|${DAY_ABBREVS.join('|')})[,\\s]+` +
      `(${MONTH_PATTERN})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:[,\\s]+(\\d{4}))?`, 'gi');
    while ((m = wdRe.exec(text)) !== null) {
      const mon = MONTH_NAMES[m[1].toLowerCase()];
      if (!mon) continue;
      const day = parseInt(m[2]);
      const year = m[3] ? parseInt(m[3]) : null;
      addDateCandidate(candidates, seen, year, mon, day, m[0].trim(), 25 + conf + metaPen, li);
    }

    // 2. Month Day, Year: "February 15, 2026"
    const myRe = new RegExp(
      `\\b(${MONTH_PATTERN})\\s+(\\d{1,2})(?:st|nd|rd|th)?[,\\s]+(\\d{4})\\b`, 'gi');
    while ((m = myRe.exec(text)) !== null) {
      const mon = MONTH_NAMES[m[1].toLowerCase()];
      if (mon) addDateCandidate(candidates, seen, parseInt(m[3]), mon, parseInt(m[2]),
        m[0].trim(), 15 + conf + metaPen, li);
    }

    // 3. ISO: 2026-02-15
    const isoRe = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
    while ((m = isoRe.exec(text)) !== null) {
      addDateCandidate(candidates, seen, parseInt(m[1]), parseInt(m[2]), parseInt(m[3]),
        m[0], 12 + conf + metaPen, li);
    }

    // 4. Month Day (no year): "February 8"
    const mdRe = new RegExp(`\\b(${MONTH_PATTERN})\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, 'gi');
    while ((m = mdRe.exec(text)) !== null) {
      const mon = MONTH_NAMES[m[1].toLowerCase()];
      if (!mon) continue;
      const after = text.slice(m.index + m[0].length, m.index + m[0].length + 10).trim();
      if (/^\d{4}/.test(after.replace(/^[,\s]+/, ''))) continue; // already captured with year
      addDateCandidate(candidates, seen, null, mon, parseInt(m[2]),
        m[0].trim(), 10 + conf + metaPen, li);
    }

    // 5. US numeric: 2/15/2026 or 2/15
    const usRe = /\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/g;
    while ((m = usRe.exec(text)) !== null) {
      const month = parseInt(m[1]);
      const day = parseInt(m[2]);
      if (month < 1 || month > 12) continue;
      let year = m[3] ? parseInt(m[3]) : null;
      if (year !== null && year < 100) year += 2000;
      addDateCandidate(candidates, seen, year, month, day, m[0], 8 + conf + metaPen, li);
    }

    // 6. Day-of-week alone: "Sunday"
    const dowRe = new RegExp(
      `\\b(?:next\\s+)?(${DAY_NAMES.join('|')}|${DAY_ABBREVS.join('|')})\\b`, 'gi');
    while ((m = dowRe.exec(text)) !== null) {
      const name = m[1].toLowerCase();
      let idx = DAY_NAMES.indexOf(name);
      if (idx === -1) idx = DAY_ABBREVS.indexOf(name);
      if (idx === -1) continue;
      // Skip if this day name is part of a "Weekday, Month Day" already captured
const afterSlice = text.slice(m.index + m[0].length).trimStart();
if (/^[,\s]+(?:january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)/i.test(afterSlice)) continue;
const resolved = resolveNextDayOfWeek(idx);
addDateCandidate(candidates, seen, resolved.year, resolved.month, resolved.day,
  m[0].trim(), 5 + conf + metaPen, li);
    }

    // 7. Relative: "today" / "tomorrow"
    if (/\btoday\b/i.test(text)) {
      const d = new Date();
      addDateCandidate(candidates, seen, d.getFullYear(), d.getMonth() + 1, d.getDate(),
        'today', 10 + conf, li);
    }
    if (/\btomorrow\b/i.test(text)) {
      const d = new Date(); d.setDate(d.getDate() + 1);
      addDateCandidate(candidates, seen, d.getFullYear(), d.getMonth() + 1, d.getDate(),
        'tomorrow', 10 + conf, li);
    }
  }

  candidates.sort((a, b) => b.score - a.score);
  return candidates;
}

function addDateCandidate(arr, seen, year, month, day, label, score, lineIdx) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return;
  const y = resolveYear(month, day, year);
  const dateStr = `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  if (seen.has(dateStr)) return;
  seen.add(dateStr);
  arr.push({ date: dateStr, startTime: null, endTime: null, label, score, lineIdx });
}

// =============================================================================
// Time candidate scoring
// =============================================================================

function scoreTimeCandidates(lines) {
  const candidates = [];

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    const text = line.text;
    const conf = (line.confidence || 50) / 10;
    const metaPen = EMAIL_META_RE.test(text) ? -10 : 0;
    let m;

    // 1a. Range with colons + at least end am/pm: "2:00 PM - 3:30 PM" or "2:00-3:30 PM"
    const rcRe = /(\d{1,2}):(\d{2})\s*(am|pm)?\s*[-\u2013\u2014]+\s*(\d{1,2}):(\d{2})\s*(am|pm)/gi;
    while ((m = rcRe.exec(text)) !== null) {
      const endAP = m[6];
      const startAP = m[3] || endAP;
      let st = to24h(parseInt(m[1]), parseInt(m[2]), startAP);
      const et = to24h(parseInt(m[4]), parseInt(m[5]), endAP);
      if (st >= et && !m[3]) st = to24h(parseInt(m[1]), parseInt(m[2]), flipPeriod(endAP));
      candidates.push({ startTime: st, endTime: et, label: m[0].trim(),
        score: 30 + conf + metaPen, lineIdx: li, isRange: true });
    }

    // 1b. Range with colons, NO am/pm (24h): "14:00-23:00"
    const rc24Re = /(\d{1,2}):(\d{2})\s*[-\u2013\u2014]+\s*(\d{1,2}):(\d{2})(?!\s*(?:am|pm))/gi;
    while ((m = rc24Re.exec(text)) !== null) {
      const h1 = parseInt(m[1]), h2 = parseInt(m[3]);
      if (h1 > 23 || h2 > 23) continue;
      candidates.push({ startTime: to24h(h1, parseInt(m[2]), null),
        endTime: to24h(h2, parseInt(m[4]), null), label: m[0].trim(),
        score: 15 + conf + metaPen, lineIdx: li, isRange: true });
    }

    // 2a. Range without colons, both am/pm: "6pm-11pm"
    const rnbRe = /(\d{1,2})\s*(am|pm)\s*[-\u2013\u2014]+\s*(\d{1,2})\s*(am|pm)/gi;
    while ((m = rnbRe.exec(text)) !== null) {
      candidates.push({ startTime: to24h(parseInt(m[1]), 0, m[2]),
        endTime: to24h(parseInt(m[3]), 0, m[4]), label: m[0].trim(),
        score: 30 + conf + metaPen, lineIdx: li, isRange: true });
    }

    // 2b. Range without colons, end-only am/pm: "6-11 pm" / "6–11pm"
    const rneRe = /(\d{1,2})\s*[-\u2013\u2014]+\s*(\d{1,2})\s*(am|pm)/gi;
    while ((m = rneRe.exec(text)) !== null) {
      // Skip if start already had am/pm (caught by 2a)
      const before = text.slice(Math.max(0, m.index - 3), m.index + String(m[1]).length);
      if (/am|pm/i.test(before.slice(String(m[1]).length))) continue;
      const ap = m[3];
      let st = to24h(parseInt(m[1]), 0, ap);
      const et = to24h(parseInt(m[2]), 0, ap);
      if (st >= et) st = to24h(parseInt(m[1]), 0, flipPeriod(ap));
      candidates.push({ startTime: st, endTime: et, label: m[0].trim(),
        score: 25 + conf + metaPen, lineIdx: li, isRange: true });
    }

    // 3. Range with "to": "2:00 PM to 3:30 PM"
    const rtoRe = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s+to\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/gi;
    while ((m = rtoRe.exec(text)) !== null) {
      if (!m[3] && !m[6]) continue; // require at least one am/pm
      const endAP = m[6] || m[3];
      const startAP = m[3] || endAP;
      let st = to24h(parseInt(m[1]), m[2] ? parseInt(m[2]) : 0, startAP);
      const et = to24h(parseInt(m[4]), m[5] ? parseInt(m[5]) : 0, endAP);
      if (st >= et && !m[3]) st = to24h(parseInt(m[1]), m[2] ? parseInt(m[2]) : 0, flipPeriod(endAP));
      candidates.push({ startTime: st, endTime: et, label: m[0].trim(),
        score: 25 + conf + metaPen, lineIdx: li, isRange: true });
    }

    // 4. Single time with "at" + am/pm: "at 3:00 PM"
    const atRe = /\bat\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)/gi;
    while ((m = atRe.exec(text)) !== null) {
      candidates.push({ startTime: to24h(parseInt(m[1]), m[2] ? parseInt(m[2]) : 0, m[3]),
        endTime: null, label: m[0].trim(), score: 15 + conf + metaPen, lineIdx: li, isRange: false });
    }

    // 5. Single time with am/pm: "3:00 PM" or "3pm"
    const singleRe = /\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/gi;
    while ((m = singleRe.exec(text)) !== null) {
      const t = to24h(parseInt(m[1]), m[2] ? parseInt(m[2]) : 0, m[3]);
      candidates.push({ startTime: t, endTime: null, label: m[0].trim(),
        score: 10 + conf + metaPen, lineIdx: li, isRange: false });
    }

    // 6. Single time without am/pm: "9:41" — low score, may be device clock
    const bareRe = /\b(\d{1,2}):(\d{2})\b/g;
    while ((m = bareRe.exec(text)) !== null) {
      const h = parseInt(m[1]);
      if (h > 23) continue;
      // Penalise if near top of image (device clock)
      const clockPen = line.yNorm < 0.1 ? -25 : 0;
      candidates.push({ startTime: to24h(h, parseInt(m[2]), null), endTime: null,
        label: m[0], score: 3 + conf + metaPen + clockPen, lineIdx: li, isRange: false });
    }
  }

  // Deduplicate: keep highest score per unique (startTime, endTime)
  const best = new Map();
  for (const c of candidates) {
    const key = `${c.startTime}|${c.endTime || ''}`;
    if (!best.has(key) || c.score > best.get(key).score) best.set(key, c);
  }
  const deduped = [...best.values()];
  deduped.sort((a, b) => b.score - a.score);
  return deduped;
}

// =============================================================================
// Coherent date + time selection
// =============================================================================

function selectCoherentDateTime(dateCandidates, timeCandidates) {
  if (dateCandidates.length === 0) return { bestDate: null, bestTime: timeCandidates[0] || null };
  if (timeCandidates.length === 0) return { bestDate: dateCandidates[0], bestTime: null };

  let bestScore = -Infinity;
  let bestDate = dateCandidates[0];
  let bestTime = timeCandidates[0];

  for (const dc of dateCandidates.slice(0, 5)) {
    for (const tc of timeCandidates.slice(0, 5)) {
      let pair = dc.score + tc.score;
      if (dc.lineIdx !== undefined && tc.lineIdx !== undefined) {
        const dist = Math.abs(dc.lineIdx - tc.lineIdx);
        if (dist === 0) pair += 15;
        else if (dist === 1) pair += 10;
        else if (dist <= 3) pair += 5;
      }
      if (pair > bestScore) { bestScore = pair; bestDate = dc; bestTime = tc; }
    }
  }
  return { bestDate, bestTime };
}

// =============================================================================
// Location candidate scoring
// =============================================================================

function scoreLocationCandidates(lines, usedLines) {
  const candidates = [];

  for (let li = 0; li < lines.length; li++) {
    if (usedLines.has(li)) continue;
    const text = lines[li].text.trim();
    if (!text) continue;
    const conf = (lines[li].confidence || 50) / 10;
    let score = 0;
    let matched = false;

    // "Location:" label
    const labelRe = /^(?:location|where|place|venue|address)\s*:\s*(.+)/i;
    const lm = text.match(labelRe);
    if (lm) {
      candidates.push({ text: lm[1].trim(), score: 40 + conf, lineIdx: li });
      continue;
    }

    // Street address
    if (/\b\d+\s+\w+\s+(?:St|Ave|Blvd|Dr|Rd|Ln|Way|Court|Ct|Place|Pl|Street|Avenue|Boulevard|Drive|Road|Lane)\.?\b/i.test(text)) {
      score += 30; matched = true;
    }

    // Room / Building keywords
    if (/\b(?:Room|Building|Bldg|Floor|Suite|Ste)\b/i.test(text)) {
      if (!/\b\d{1,2}:\d{2}\b/.test(text) && !/\b\d{4}\b/.test(text)) {
        score += 20; matched = true;
      }
    }

    // "Place, City" pattern — capitalised words around comma
    if (/[A-Z][a-z]+(?:\s+[A-Za-z']+)*,\s+[A-Z][a-z]/.test(text)) {
      // Make sure it doesn't look like a date
      if (!hasStrongDatePattern(text)) {
        score += 20; matched = true;
      }
    }

    // Penalise lines with date/time content
    if (hasStrongDatePattern(text)) score -= 20;
    if (hasTimePattern(text)) score -= 20;
    if (EMAIL_META_RE.test(text)) score -= 15;

    if (matched) {
      candidates.push({ text, score: score + conf, lineIdx: li });
    }
  }

  candidates.sort((a, b) => b.score - a.score);
  return candidates;
}

// =============================================================================
// Title candidate scoring
// =============================================================================

function scoreTitleCandidates(lines, headlineLines, usedLines) {
  const headTexts = new Set(headlineLines.map(l => l.text.trim().toLowerCase()));
  const medianH = getMedianHeight(lines);
  const candidates = [];

  for (let li = 0; li < lines.length; li++) {
    if (usedLines.has(li)) continue;
    const line = lines[li];
    const text = line.text.trim();
    if (text.length < 3) continue;

    let score = 0;

    // Headline crop bonus
    if (headTexts.has(text.toLowerCase())) score += 20;

    // Large font bonus
    if (medianH > 0 && line.heightNorm > medianH * 1.5) score += 15;
    if (medianH > 0 && line.heightNorm > medianH * 2.0) score += 10;

    // Upper content area bonus
    if (line.yNorm >= 0.05 && line.yNorm <= 0.4) score += 10;

    // Penalties
    if (EMAIL_META_RE.test(text)) score -= 30;
    if (/@/.test(text)) score -= 20;
    if (text.length < 5) score -= 5;
    if (/^\d+$/.test(text)) score -= 20;
    if (hasStrongDatePattern(text)) score -= 15;
    if (hasTimePattern(text)) score -= 10;

    // Clean the text (strip inline time patterns)
    let cleaned = text.replace(/\b\d{1,2}(?::\d{2})?\s*(?:am|pm)\b/gi, '').trim();
    cleaned = cleaned.replace(/^[-\u2013\u2014:,.\s]+|[-\u2013\u2014:,.\s]+$/g, '').trim();
    if (cleaned.length < 3 || /^\d+$/.test(cleaned)) continue;

    candidates.push({ text: cleaned.slice(0, 100), score, lineIdx: li });
  }

  candidates.sort((a, b) => b.score - a.score);
  return candidates;
}

// =============================================================================
// Timezone extraction (unchanged logic)
// =============================================================================

export function extractTimezone(text) {
  const tzAbbrRe = /\b(EST|EDT|CST|CDT|MST|MDT|PST|PDT|ET|CT|MT|PT|UTC|GMT)\b/i;
  const m = text.match(tzAbbrRe);
  if (m) return TZ_MAP[m[1].toUpperCase()] || null;
  const mi = text.match(/\b(America|Europe|Asia|Pacific|Africa|Australia)\/[\w\-/]+\b/);
  if (mi) return mi[0];
  return null;
}

// =============================================================================
// Helpers
// =============================================================================

function hasStrongDatePattern(text) {
  const re = new RegExp(
    `(?:${DAY_NAMES.join('|')}|${DAY_ABBREVS.join('|')})[,\\s]+(?:${MONTH_PATTERN})` +
    `|\\b(?:${MONTH_PATTERN})\\s+\\d{1,2}` +
    `|\\b\\d{1,2}/\\d{1,2}` +
    `|\\b\\d{4}-\\d{2}-\\d{2}\\b`, 'i');
  return re.test(text);
}

function hasTimePattern(text) {
  return /\b\d{1,2}(?::\d{2})?\s*(?:am|pm)\b/i.test(text) ||
    /\d{1,2}:\d{2}\s*[-\u2013\u2014]+\s*\d{1,2}/.test(text);
}

function getMedianHeight(lines) {
  if (lines.length === 0) return 0;
  const heights = lines.map(l => l.heightNorm).sort((a, b) => a - b);
  const mid = Math.floor(heights.length / 2);
  return heights.length % 2 === 0 ? (heights[mid - 1] + heights[mid]) / 2 : heights[mid];
}

function resolveYear(month, day, explicitYear) {
  if (explicitYear) return explicitYear;
  const now = new Date();
  const thisYear = now.getFullYear();
  const candidate = new Date(thisYear, month - 1, day);
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  return candidate < yesterday ? thisYear + 1 : thisYear;
}

function resolveNextDayOfWeek(targetDayIndex) {
  const now = new Date();
  let daysAhead = targetDayIndex - now.getDay();
  if (daysAhead <= 0) daysAhead += 7;
  const target = new Date(now); target.setDate(now.getDate() + daysAhead);
  return { year: target.getFullYear(), month: target.getMonth() + 1, day: target.getDate() };
}

export function addMinutes(time24h, minutes) {
  const [h, m] = time24h.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function to24h(hours, minutes, ampm) {
  let h = hours;
  if (ampm) {
    const u = ampm.toUpperCase();
    if (u === 'PM' && h !== 12) h += 12;
    if (u === 'AM' && h === 12) h = 0;
  }
  return `${String(h).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function flipPeriod(ampm) {
  if (!ampm) return null;
  return ampm.toLowerCase() === 'am' ? 'pm' : 'am';
}

function computeConfidence(dateCandidates, timeCandidates, startTime) {
  const hasDate = dateCandidates.length > 0;
  const hasTime = !!startTime;
  const topDateScore = dateCandidates[0]?.score ?? 0;
  const topTimeScore = timeCandidates[0]?.score ?? 0;
  if (hasDate && hasTime && topDateScore >= 15 && topTimeScore >= 15) return 'high';
  if (hasDate && hasTime) return 'medium';
  if (hasDate || hasTime) return 'medium';
  return 'low';
}
